import React, { memo, useCallback, useEffect, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { DevTool } from '@hookform/devtools';
import useEditTemplateForm from './useEditTemplateForm';
import TextField from '../../../components/TextField';
import CustomFormatForm from '../../../components/CustomFormatForm';
import { Button } from '../../../components';
import { Template } from '../../../@types/domain';
import * as S from '../style';

type TemplateFormProperties = {
  template: Template;
};

function TemplateForm({ template }: TemplateFormProperties) {
  const { control, register, unregister, setValue, onSubmit, formState } =
    useEditTemplateForm(template);
  const { errors, isSubmitting, isValid } = formState;

  useEffect(() => {
    register('columns');
    setValue('columns', template.columns || []);
    return () => {
      unregister('columns');
    };
  }, [register, setValue, template, unregister]);

  const [templateColumns, setTemplateColumns] = useState(template.columns);

  const handleDragEndCustomFormat = useCallback(
    ({ destination, source }) => {
      if (!destination) {
        return;
      }

      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      )
        return;

      const newCF = [...templateColumns];

      const [removed] = newCF.splice(source.index, 1);
      newCF.splice(destination.index, 0, removed);

      setTemplateColumns(newCF);
      setValue('columns', newCF, { shouldValidate: true });
    },
    [setValue, templateColumns]
  );

  useEffect(() => {
    setValue('columns', templateColumns, { shouldValidate: true });
  }, [templateColumns, setValue]);

  return (
    <form onSubmit={onSubmit}>
      <S.Label>Template name:</S.Label>
      <TextField
        type="text"
        placeholder="Type a template name..."
        disabled={isSubmitting}
        error={!!errors.name}
        autoFocus={true}
        {...register('name')}
      />
      {errors.name && <S.ErrorText>{errors.name.message}</S.ErrorText>}
      <S.Label>Template columns:</S.Label>
      {templateColumns && (
        <DragDropContext
          onDragEnd={handleDragEndCustomFormat}
          key={template.id + template.columns.length}
        >
          <CustomFormatForm
            editing={false}
            elements={templateColumns}
            setElements={setTemplateColumns}
            warning="columns"
            context="customFormat"
            displayField="columnName"
            placeholder="Type a column name..."
          />
        </DragDropContext>
      )}
      <Button type="submit" disabled={isSubmitting || !isValid}>
        Save
      </Button>
      <DevTool control={control} />
    </form>
  );
}

export default memo(TemplateForm);
